/**
 * ReviewQueueTable — the desktop queue table for one section (BON-381).
 *
 * Columns: AGE · REASON · BOOKING|SESSION · WORKSHOP · AMOUNT · action.
 * Departures from the design prototype, each settled: the reason is free text
 * rather than a coded pill (and is clamped to two lines here, because triage
 * needs the rhythm more than it needs the full sentence — the detail page shows
 * it whole), the first column is an age in days with the absolute instant on its
 * title, and there is no per-row Resolve, since resolving requires a note.
 *
 * The row itself is NOT clickable. Opening the review is the one primary button;
 * the reference and the workshop are their own links. A `<tr onClick>` wrapping
 * inner links is ambiguous about what was hit, and is neither focusable nor
 * announced without re-implementing what a real button already gives us.
 */
import type { WorkshopShell } from '../dashboards/workshopDetailUrlFor';

import ReviewQueueRow from './components/ReviewQueueRow';
import type { ReviewKind, ReviewQueueItem } from './reviewTypes';

export { REASON_CLAMP_STYLE } from './reviewQueueRowStyle';

export interface ReviewQueueTableProps {
  rows: ReviewQueueItem[];
  kind: ReviewKind;
  /** Which dashboards shell the workshop links target. */
  shell: WorkshopShell;
  /** Resolved rows: dimmed, stamped, no action button. */
  muted?: boolean;
}

export function ReviewQueueTable({ rows, kind, shell, muted = false }: ReviewQueueTableProps) {
  const noun = kind === 'booking' ? 'Booking' : 'Session';
  return (
    // U17: a name and real column semantics. Two of these tables sit in one card
    // per section, and both were anonymous — a screen-reader user landing in one
    // could not tell which. `scope="col"` is what lets a cell be announced with
    // its heading; without it a row reads as six bare values.
    //
    // The LAST column is deliberately `width` and `text-align` matched to the open
    // table (U16): the two used to differ (110 vs 268), so the open and resolved
    // tables in one card did not line up.
    <table
      className="nb-tbl"
      aria-label={`${noun}s needing review${muted ? ' — resolved' : ''}`}
      // The floor the section's scroll container scrolls against (U15). Roughly
      // the sum of the fixed column widths plus room for the two free ones; below
      // this the columns compress and the reason cell wins nothing.
      style={{ minWidth: 880 }}
    >
      <thead>
        <tr>
          <th scope="col" style={{ paddingLeft: 18, width: 96 }}>
            Age
          </th>
          <th scope="col" style={{ width: 280 }}>
            Reason
          </th>
          <th scope="col">{noun}</th>
          <th scope="col">Workshop</th>
          <th scope="col" style={{ textAlign: 'right', width: 108 }}>
            Amount
          </th>
          <th scope="col" style={{ textAlign: 'right', paddingRight: 18, width: 268 }}>
            {muted ? 'Resolved by' : <span className="nb-sr-only">Actions</span>}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((item) => (
          <ReviewQueueRow key={item.id} item={item} shell={shell} muted={muted} />
        ))}
      </tbody>
    </table>
  );
}
